import { useCallback, useRef, useState } from 'react';

import { usePdfTab } from './usePdfTab';

/** Where a resolved PDF lives: an in-memory blob, or a URL to navigate to. */
export type PdfSource = { blob: Blob } | { url: string };

/** Marker on an error a `resolve()` throws after it has already shown its own
 * user-facing message, so `exportPdf` closes the tab silently instead of
 * showing the generic export-failed toast. */
export interface HandledExportError extends Error {
  pdfExportHandled: true;
}

/** Build an error that tells `exportPdf` the failure was already reported. */
export function handledExportError(message: string): HandledExportError {
  return Object.assign(new Error(message), {
    pdfExportHandled: true as const,
  });
}

function isHandledExportError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { pdfExportHandled?: unknown }).pdfExportHandled === true
  );
}

export interface UsePdfExportResult {
  /**
   * Open the PDF produced by `resolve()` in a new browser tab, tracking loading
   * by `id`. `resolve` returns the PDF source, or `null` when there is nothing
   * to show (which surfaces the generic export-failed toast).
   *
   * Must be called from inside a synchronous click handler: the tab is opened
   * before `resolve` is awaited so the browser treats it as user-initiated and
   * does not block the popup. `context` labels console errors for grep-ability.
   */
  exportPdf: (
    id: string,
    resolve: () => Promise<PdfSource | null> | PdfSource | null,
    context?: string,
    options?: ExportPdfOptions
  ) => Promise<void>;
  /** Id currently exporting, or null when idle. */
  exportingId: string | null;
}

export interface ExportPdfOptions {
  /**
   * Optional gate run after the (popup-safe) tab opens but before `resolve`.
   * Return `false` to abort the export: the tab is closed silently with no
   * toast, so a caller that already surfaced its own feedback (e.g. a failed
   * autosave flush) does not double-report. Runs while `exportingId` is set so
   * the trigger shows its loading state during the check.
   */
  preflight?: () => Promise<boolean>;
}

/**
 * Shared plumbing for "open a PDF in a new tab" actions: popup-safe tab
 * opening, loading-by-id, a double-fire guard, and uniform success/failure
 * handling. Callers supply only a `resolve` that yields the PDF source —
 * dispatch logic (by task type, by order variant, ...) lives in the caller.
 */
export function usePdfExport(): UsePdfExportResult {
  const openPdfTab = usePdfTab();
  const [exportingId, setExportingId] = useState<string | null>(null);
  // Ref guard blocks a double-fire without making `exportPdf` depend on — and
  // be recreated by — the loading state.
  const inFlightIdRef = useRef<string | null>(null);

  const exportPdf = useCallback(
    async (
      id: string,
      resolve: () => Promise<PdfSource | null> | PdfSource | null,
      context = 'PDF',
      options?: ExportPdfOptions
    ): Promise<void> => {
      if (inFlightIdRef.current === id) return;

      // Open the tab synchronously, before any await, so the browser treats it
      // as user-initiated and does not block the popup.
      const tab = openPdfTab();
      if (!tab) return;

      inFlightIdRef.current = id;
      setExportingId(id);
      try {
        if (options?.preflight && !(await options.preflight())) {
          // Caller vetoed the export (and owns any user-facing message), so
          // close the placeholder tab without the generic export-failed toast.
          tab.close();
          return;
        }
        const source = await resolve();
        if (!source) {
          tab.fail(context, new Error('no PDF source resolved'));
          return;
        }
        if ('url' in source) {
          tab.navigateToUrl(source.url);
        } else {
          tab.setBlob(source.blob);
        }
      } catch (e) {
        if (isHandledExportError(e)) {
          // The caller already surfaced a specific, user-facing message, so
          // close the placeholder tab silently rather than double-reporting
          // with the generic export-failed toast.
          tab.close();
        } else {
          tab.fail(context, e);
        }
      } finally {
        inFlightIdRef.current = null;
        setExportingId(null);
      }
    },
    [openPdfTab]
  );

  return { exportPdf, exportingId };
}
